import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  createKlarnaSession,
  updateKlarnaSession,
  addKlarnaPaymentToken,
} from "@/networking/payments.api";

interface KlarnaLoadResult {
  show_form: boolean;
}

interface KlarnaAuthorizeResult {
  approved: boolean;
  authorization_token?: string;
  error?: string;
}

declare global {
  interface Window {
    Klarna: {
      Payments: {
        init: (options: { client_token: string }) => Promise<void>;
        load: (
          options: { container: string; payment_method_category: string },
          data: Record<string, unknown>,
          callback: (result: KlarnaLoadResult) => void,
        ) => void;
        authorize: (
          options: { payment_method_category: string },
          data: Record<string, unknown>,
          callback: (result: KlarnaAuthorizeResult) => void,
        ) => void;
      };
    };
  }
}

interface KlarnaCheckoutProps {
  cartToken: string;
  onSuccess: () => Promise<void>;
  setPaymentError: (msg?: string) => void;
}

export function useKlarnaCheckout() {
  const [klarnaModalLoading, setLoading] = useState(false);
  const addTokenMutation = useMutation({ mutationFn: addKlarnaPaymentToken });

  async function handleKlarnaCheckout({
    cartToken,
    onSuccess,
    setPaymentError,
  }: KlarnaCheckoutProps) {
    try {
      setLoading(true);

      const sess = await createKlarnaSession({ cartToken });
      if (!sess.success) {
        setPaymentError("Could not create Klarna session.");
        return;
      }

      if (!window.Klarna?.Payments) {
        setPaymentError("Klarna JS not loaded.");
        return;
      }
      await window.Klarna.Payments.init({ client_token: sess.client_token });

      const containerId = "klarna-payments-container";
      if (!document.getElementById(containerId)) {
        const div = document.createElement("div");
        div.id = containerId;
        div.style.display = "none";
        document.body.appendChild(div);
      }

      await new Promise<void>((res, rej) => {
        window.Klarna.Payments.load(
          {
            container: `#${containerId}`,
            payment_method_category: "pay_later",
          },
          {},
          (lr: KlarnaLoadResult) =>
            lr.show_form ? res() : rej(new Error("load failed")),
        );
      });

      const authResult = await new Promise<KlarnaAuthorizeResult>(
        (res, rej) => {
          window.Klarna.Payments.authorize(
            { payment_method_category: "pay_later" },
            {},
            (ar: KlarnaAuthorizeResult) =>
              ar.approved ? res(ar) : rej(ar.error || "not approved"),
          );
        },
      );

      if (!authResult.authorization_token) {
        throw new Error("No authorization token received from Klarna");
      }

      updateKlarnaSession({ cartToken, sessionId: sess.session_id }).catch(
        () => {},
      );

      await addTokenMutation.mutateAsync({
        cartToken,
        authorization_token: authResult.authorization_token,
        session_id: sess.session_id,
      });

      await onSuccess();
    } catch (err: unknown) {
      console.error("Klarna error:", err);
      setPaymentError(
        typeof err === "string" ? err : "There was a problem with Klarna.",
      );
    } finally {
      setLoading(false);
    }
  }

  return { handleKlarnaCheckout, klarnaModalLoading };
}
